/*
  JV Furniture: page size follows the grid view (collection and search pages).

  The large-image view uses the normal template. The compact view (more products per row, desktop only)
  uses the "dense" copy of the template, which loads more products per page. The copy is chosen with
  ?view=dense in the address, so page links, filters and sorting all stay on the same page size.

  Nothing about how either view looks is changed here. When the view is switched, the list for the other
  page size is fetched and put in place, keeping the shopper at roughly the same point in the collection.
*/
import { morph, MORPH_OPTIONS } from '@theme/morph';

const PARAM = 'view';
const DENSE = 'dense';
const STANDARD = 'standard';
const SECTION_PREFIX = 'shopify-section-';
const DEFAULT_SIZES = { standard: 35, dense: 44 };
const desktop = window.matchMedia('(min-width: 750px)');

const read = (key) => {
  try {
    return window.sessionStorage.getItem(key);
  } catch (error) {
    return null;
  }
};
const write = (key, value) => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (error) {
    /* storage unavailable: the page still works, it just looks the section up again next time */
  }
};

const list = () => document.querySelector('results-list[data-jv-view]');
const viewOf = (element) => (element && element.dataset.jvView === DENSE ? DENSE : STANDARD);

/* Which page size the current grid view calls for. */
function wantedView(chosen) {
  if (!desktop.matches) return STANDARD;
  let layout = chosen;
  if (!layout) {
    const grid = document.querySelector('results-list[data-jv-view] .product-grid');
    layout = grid ? grid.getAttribute('product-grid-view') : read('product-grid-view-desktop');
  }
  return layout === 'zoom-out' ? DENSE : STANDARD;
}

/* The theme rebuilds the address from the filter form when a filter or the sort order changes.
   While the compact view is showing, keep it on the compact template. */
const nativePushState = window.history.pushState.bind(window.history);
window.history.pushState = function (state, title, url) {
  if (url != null && viewOf(list()) === DENSE) {
    try {
      const next = new URL(String(url), window.location.href);
      if (
        next.origin === window.location.origin &&
        next.pathname === window.location.pathname &&
        !next.searchParams.has(PARAM)
      ) {
        next.searchParams.set(PARAM, DENSE);
        url = next.toString();
      }
    } catch (error) {
      /* leave the address as the theme gave it */
    }
  }
  return nativePushState(state, title, url);
};

function sizeOf(view, element) {
  if (element && viewOf(element) === view) {
    const shown = Number(element.dataset.jvPerPage);
    if (shown > 0) return shown;
  }
  const saved = Number(read(`jv-grid-size:${view}`));
  return saved > 0 ? saved : DEFAULT_SIZES[view];
}

async function fetchDocument(url) {
  const response = await fetch(url.toString(), { credentials: 'same-origin' });
  if (!response.ok) return null;
  return new DOMParser().parseFromString(await response.text(), 'text/html');
}

/* Fetches the list section as the other template draws it. The section's id differs between the two templates,
   so it is looked up once from a full page and remembered for the rest of the visit. */
async function fetchSection(url, view, templateName) {
  const key = `jv-grid-section:${templateName}:${view}`;
  const knownId = read(key);
  if (knownId) {
    const sectionUrl = new URL(url);
    sectionUrl.searchParams.set('section_id', knownId);
    const doc = await fetchDocument(sectionUrl).catch(() => null);
    const section = doc && doc.getElementById(SECTION_PREFIX + knownId);
    if (section && viewOf(section.querySelector('results-list[data-jv-view]')) === view) return section;
  }
  const doc = await fetchDocument(url);
  const results = doc && doc.querySelector('results-list[data-jv-view]');
  const section = results && results.closest('.shopify-section');
  if (!section || viewOf(results) !== view) return null;
  write(key, section.id.replace(SECTION_PREFIX, ''));
  return section;
}

let run = 0;

async function sync(chosen) {
  const current = list();
  if (!current) return;
  const have = viewOf(current);
  const perPage = Number(current.dataset.jvPerPage);
  if (perPage > 0 && perPage < 250) write(`jv-grid-size:${have}`, String(perPage));

  const want = wantedView(chosen);
  if (want === have) return;

  const section = current.closest('.shopify-section');
  if (!section) return;

  const attempt = ++run;
  const startedAt = window.location.href;
  const url = new URL(startedAt);
  if (want === DENSE) url.searchParams.set(PARAM, DENSE);
  else url.searchParams.delete(PARAM);

  /* Stay at the same point in the collection: the page that holds the first product now on screen. */
  const page = Number(url.searchParams.get('page')) || 1;
  if (page > 1) {
    const firstItem = sizeOf(have, current) * (page - 1);
    const nextPage = Math.floor(firstItem / sizeOf(want)) + 1;
    if (nextPage > 1) url.searchParams.set('page', String(nextPage));
    else url.searchParams.delete('page');
  }

  let fresh = null;
  try {
    fresh = await fetchSection(url, want, current.dataset.jvTemplate || 'collection');
  } catch (error) {
    fresh = null;
  }
  if (!fresh || attempt !== run) return;

  /* A filter or sort was applied while this was loading: start again from the new address. */
  if (window.location.href !== startedAt) {
    sync();
    return;
  }

  const grid = section.querySelector('.product-grid');
  const layout = grid ? grid.getAttribute('product-grid-view') : null;

  morph(section, fresh, MORPH_OPTIONS);
  section.id = fresh.id;
  section.className = fresh.className;

  const newGrid = section.querySelector('.product-grid');
  if (newGrid && layout && newGrid.getAttribute('product-grid-view') !== layout) {
    newGrid.setAttribute('product-grid-view', layout);
  }

  window.history.replaceState(window.history.state, '', url.toString());
}

let timer = 0;
document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement) || target.name !== 'grid' || !target.checked) return;
  const chosen = target.value;
  window.clearTimeout(timer);
  /* Wait for the theme's own change of layout to finish before the list is refreshed. */
  timer = window.setTimeout(() => sync(chosen), 500);
});

desktop.addEventListener('change', () => {
  window.clearTimeout(timer);
  timer = window.setTimeout(() => sync(), 500);
});

window.addEventListener('pageshow', (event) => {
  if (event.persisted) sync();
});

sync();
